import React, { useEffect, useRef } from "react";
import { Box, Typography } from "@mui/material";

export const REEL_FILTERS = [
  { id: "none", labelKey: "filterNone", css: "none" },
  { id: "vivid", labelKey: "filterVivid", css: "saturate(1.45) contrast(1.05)" },
  { id: "warm", labelKey: "filterWarm", css: "sepia(0.3) saturate(1.25)" },
  { id: "cool", labelKey: "filterCool", css: "saturate(0.9) hue-rotate(-14deg) brightness(1.03)" },
  { id: "bw", labelKey: "filterBw", css: "grayscale(1)" },
  { id: "retro", labelKey: "filterRetro", css: "sepia(0.6) contrast(0.9) brightness(1.05)" },
  { id: "drama", labelKey: "filterDrama", css: "contrast(1.3) saturate(1.1) brightness(0.95)" },
];

export const REEL_SPEEDS = [0.5, 1, 1.5, 2];
export const REEL_TEXT_COLORS = ["#ffffff", "#000000", "#00d4ff", "#ffe14d", "#ff4b4b"];

export const getReelFilterCss = (id) =>
  REEL_FILTERS.find((filter) => filter.id === id)?.css || "none";

// Los videos grabados en el navegador (WebM) no informan su duración hasta recorrerlos.
export const resolveVideoDuration = (video) =>
  new Promise((resolve) => {
    if (Number.isFinite(video.duration)) {
      resolve(video.duration);
      return;
    }
    const finish = () => {
      clearTimeout(timeout);
      video.removeEventListener("durationchange", onChange);
      const duration = Number.isFinite(video.duration) ? video.duration : 0;
      video.currentTime = 0;
      resolve(duration);
    };
    const onChange = () => {
      if (Number.isFinite(video.duration)) finish();
    };
    const timeout = setTimeout(finish, 3000);
    video.addEventListener("durationchange", onChange);
    video.currentTime = Number.MAX_SAFE_INTEGER;
  });

// Al soltar el micrófono, Android tarda en salir del modo de llamada y el video recién
// grabado puede sonar sin audio hasta que se lo silencia y reactiva. Se repite ese gesto
// al empezar a reproducir, dos veces porque la demora varía según el teléfono.
const AUDIO_REFRESH_DELAYS_MS = [300, 1000];
const AUDIO_REFRESH_GAP_MS = 60;

export const useVideoAudioRefresh = (videoRef, videoKey, muted) => {
  const mutedRef = useRef(muted);
  mutedRef.current = muted;

  useEffect(() => {
    const video = videoRef.current;
    if (!video || !videoKey) return undefined;
    const timers = [];
    const refresh = () => {
      if (mutedRef.current) return;
      video.muted = true;
      timers.push(setTimeout(() => {
        video.muted = mutedRef.current;
      }, AUDIO_REFRESH_GAP_MS));
    };
    const handlePlaying = () => {
      AUDIO_REFRESH_DELAYS_MS.forEach((delay) => timers.push(setTimeout(refresh, delay)));
    };
    video.addEventListener("playing", handlePlaying, { once: true });
    return () => {
      video.removeEventListener("playing", handlePlaying);
      timers.forEach(clearTimeout);
    };
  }, [videoRef, videoKey]);
};

export const DEFAULT_ADJUST = { brightness: 100, contrast: 100, saturation: 100 };

const clampAdjust = (value) => {
  const number = Number(value);
  return Number.isFinite(number) ? Math.min(150, Math.max(50, number)) : 100;
};

export const buildFilterCss = (filterId, adjust) => {
  const base = getReelFilterCss(filterId);
  const parts = base === "none" ? [] : [base];
  const brightness = clampAdjust(adjust?.brightness);
  const contrast = clampAdjust(adjust?.contrast);
  const saturation = clampAdjust(adjust?.saturation);
  if (brightness !== 100) parts.push(`brightness(${brightness}%)`);
  if (contrast !== 100) parts.push(`contrast(${contrast}%)`);
  if (saturation !== 100) parts.push(`saturate(${saturation}%)`);
  return parts.length ? parts.join(" ") : "none";
};

// Solo http(s): el enlace lo escribe otro usuario y no debe poder ejecutar "javascript:".
export const getSafeLink = (value) => {
  const text = String(value || "").trim();
  if (!text) return "";
  try {
    const parsed = new URL(/^[a-z][a-z0-9+.-]*:/i.test(text) ? text : `https://${text}`);
    return ["http:", "https:"].includes(parsed.protocol) && parsed.hostname.includes(".") ? parsed.href : "";
  } catch {
    return "";
  }
};

const clampPercent = (value, fallback) => {
  const number = Number(value);
  return Number.isFinite(number) ? Math.min(100, Math.max(0, number)) : fallback;
};

// reelEdit lo escribe el cliente de otro usuario: acá solo se aceptan valores conocidos.
export const normalizeReelEdit = (post) => {
  const edit = post?.reelEdit || {};
  const trimStart = Number(edit.trimStart) > 0 ? Number(edit.trimStart) : 0;
  const trimEnd = Number(edit.trimEnd) > trimStart ? Number(edit.trimEnd) : null;
  return {
    trimStart,
    trimEnd,
    speed: REEL_SPEEDS.includes(edit.speed) ? edit.speed : 1,
    filterCss: buildFilterCss(edit.filter, edit.adjust),
    muted: edit.muted === true,
    overlays: Array.isArray(edit.overlays)
      ? edit.overlays
          .filter((overlay) => typeof overlay?.text === "string" && overlay.text.trim())
          .slice(0, 5)
          .map((overlay, index) => ({
            id: overlay.id || String(index),
            text: overlay.text.slice(0, 100),
            x: clampPercent(overlay.x, 50),
            y: clampPercent(overlay.y, 50),
            color: REEL_TEXT_COLORS.includes(overlay.color) ? overlay.color : "#ffffff",
          }))
      : [],
  };
};

export const ReelOverlayText = ({ overlay, sx, ...props }) => (
  <Typography
    {...props}
    sx={{
      position: "absolute",
      left: `${overlay.x}%`,
      top: `${overlay.y}%`,
      transform: "translate(-50%, -50%)",
      maxWidth: "90%",
      color: overlay.color,
      fontWeight: 800,
      fontSize: "clamp(18px, 6vw, 28px)",
      lineHeight: 1.2,
      textAlign: "center",
      whiteSpace: "pre-wrap",
      overflowWrap: "anywhere",
      textShadow: overlay.color === "#000000" ? "0 1px 4px rgba(255,255,255,0.7)" : "0 1px 6px rgba(0,0,0,0.75)",
      ...sx,
    }}
  >
    {overlay.text}
  </Typography>
);

export const ReelOverlays = ({ overlays }) => (
  <Box sx={{ position: "absolute", inset: 0, overflow: "hidden", pointerEvents: "none" }}>
    {overlays.map((overlay) => (
      <ReelOverlayText key={overlay.id} overlay={overlay} />
    ))}
  </Box>
);
